import { useScrollReveal } from '../../hooks/useScrollReveal';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../../components/feature/HeroSection';
import TrustBar from '../../components/feature/TrustBar';
import ServicesGrid from '../../components/feature/ServicesGrid';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import ProcessSection from '../../components/feature/ProcessSection';
import CTABand from '../../components/feature/CTABand';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreas from '../../components/feature/ServiceAreas';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';

const LOCAL_SERVICES = [
  {
    icon: 'ri-parking-line',
    title: 'Tampa Bay Parking Lot Lighting',
    description:
      'High-performance LED area lighting for commercial parking lots across Tampa, Clearwater, and St. Petersburg. Signed photometric plans engineered to meet local code requirements for foot-candle averages and uniformity ratios.',
    link: '/services',
  },
  {
    icon: 'ri-store-2-line',
    title: 'Retail & Shopping Center Lighting',
    description:
      'LED site lighting for retail centers and shopping plazas across Pinellas and Hillsborough counties. Reduced energy costs with maintained or improved light levels — and rebate acquisition managed on your behalf.',
    link: '/services',
  },
  {
    icon: 'ri-sun-line',
    title: 'Solar Lighting for Florida Properties',
    description:
      'Florida\'s year-round sunshine makes Solar All-In-One LED fixtures highly effective. Wire-free systems for parking areas, pathways, and remote commercial locations throughout the Tampa Bay region without trenching costs.',
    link: '/services',
  },
];

const AI_QAS = [
  {
    question: 'Who provides commercial LED lighting design in Tampa Bay, Florida?',
    answer:
      'Southeast Lighting & Design is the Tampa Bay area\'s full-service commercial LED lighting company. Based locally, we serve businesses in Tampa, Clearwater, St. Petersburg, Safety Harbor, and surrounding communities throughout Pinellas, Hillsborough, and Pasco counties — managing design, specification, installation, and rebates.',
  },
  {
    question: 'What areas of Tampa Bay does Southeast Lighting & Design serve?',
    answer:
      'We serve the entire Tampa Bay area — Tampa, Clearwater, St. Petersburg, Safety Harbor, and surrounding counties including Pinellas, Hillsborough, and Pasco. We also serve Seminole County, Central Florida, and have completed commercial LED lighting projects throughout the state of Florida and beyond.',
  },
  {
    question: 'Why is Southeast Lighting & Design the right choice for Tampa Bay businesses?',
    answer:
      'We are locally based with direct Tampa Bay market expertise and knowledge of Florida\'s utility rebate programs. Our team has completed commercial LED projects across the region for dealerships, retail centers, sports facilities, and industrial properties — reducing energy costs by more than 70% consistently across every project type.',
  },
];

const LOCAL_EXPERTISE = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'Locally Based in Tampa Bay',
    description:
      'Responsive local service for businesses across Tampa, Clearwater, St. Petersburg, Safety Harbor, and the surrounding Tampa Bay area — with regional knowledge and direct accountability for every project we manage.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Florida Utility Rebate Expertise',
    description:
      'We navigate Florida utility rebate programs on your behalf — maximizing available incentives and significantly reducing the net upfront cost of your commercial LED upgrade throughout the Tampa Bay area.',
  },
  {
    icon: 'ri-sun-foggy-line',
    title: 'Florida Solar LED Advantage',
    description:
      'Florida\'s subtropical climate and consistent sunshine make Solar All-In-One LED systems exceptionally effective — eliminating wiring costs and delivering reliable, high-performance lighting year-round across commercial properties.',
  },
  {
    icon: 'ri-building-line',
    title: 'All Tampa Bay Applications',
    description:
      'From Clearwater auto dealerships to Oviedo shopping centers and Safety Harbor sports courts — we serve every commercial lighting application type across the Tampa Bay region with full-service project management.',
  },
];

const PROJECTS = [
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2019/02/Alafaya-Square-1-2048x1000.jpg',
    imageAlt: 'LED site lighting at Alafaya Square retail shopping center in Oviedo, Florida',
    name: 'Alafaya Square',
    location: 'Oviedo, Florida',
    type: 'Retail Site Lighting',
    metric: '70%+',
    metricLabel: 'Energy Reduction',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2019/02/Crown-Acura-2-2048x1000.jpg',
    imageAlt: 'LED site lighting at Crown Acura dealership in Clearwater, Florida',
    name: 'Crown Acura',
    location: 'Clearwater, Florida',
    type: 'Auto Dealership Lighting',
    metric: 'LED',
    metricLabel: 'Full Upgrade',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2021/03/Canopy-Oaks-6-2048x1000.jpg',
    imageAlt: 'Solar All-In-One LED lighting installed at Canopy Oaks RV Resort in Central Florida',
    name: 'Canopy Oaks RV Resort',
    location: 'Central Florida',
    type: 'Solar All-In-One Lighting',
    metric: '0',
    metricLabel: 'Wiring Required',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2019/01/Safety-Harbor-Tennis-Court-2048x1000.jpg',
    imageAlt: 'LED sports lighting at Safety Harbor Tennis Courts — Safety Harbor, Florida',
    name: 'Safety Harbor Tennis Courts',
    location: 'Safety Harbor, Florida',
    type: 'Sports Court Lighting',
    metric: '70%+',
    metricLabel: 'Energy Saved',
  },
];

const TAMPA_AREA_GROUPS = [
  {
    region: 'Pinellas County',
    cities: ['Clearwater', 'St. Petersburg', 'Safety Harbor', 'Dunedin', 'Tarpon Springs', 'Largo', 'Seminole'],
  },
  {
    region: 'Hillsborough County',
    cities: ['Tampa', 'Brandon', 'Plant City', 'Lutz', 'Riverview', 'Temple Terrace'],
  },
  {
    region: 'Pasco County',
    cities: ['New Port Richey', 'Land O\' Lakes', 'Wesley Chapel', 'Zephyrhills', 'Dade City'],
  },
  {
    region: 'Central & South Florida',
    cities: ['Oviedo', 'Orlando', 'Winter Haven', 'Lake Wales', 'Sarasota', 'Fort Myers'],
  },
];

const FAQ_ITEMS = [
  {
    question: 'Who provides commercial LED lighting near Tampa Bay, Florida?',
    answer:
      'Southeast Lighting & Design is based in Tampa Bay and provides full-service commercial LED lighting across Tampa, Clearwater, St. Petersburg, Safety Harbor, and surrounding communities. We handle photometric design, product specification, installation coordination, and rebate acquisition for Tampa Bay commercial and industrial properties of all sizes.',
  },
  {
    question: 'Does Southeast Lighting serve Clearwater and Pinellas County?',
    answer:
      'Yes. We have completed commercial LED lighting projects throughout Pinellas County, including Clearwater, St. Petersburg, Safety Harbor, Dunedin, and Tarpon Springs. We are familiar with local permit requirements and available utility rebate programs in the Pinellas County area and can serve any commercial property type there.',
  },
  {
    question: 'What is the typical cost of commercial LED lighting in Tampa Bay?',
    answer:
      'Commercial LED lighting costs in Tampa Bay vary by scope, fixture quantity, and application type. Most Tampa Bay businesses achieve full ROI within two to five years through energy savings. Florida utility rebates can further reduce upfront investment significantly. We provide complimentary, no-obligation assessments for all qualifying Tampa Bay commercial properties.',
  },
  {
    question: 'How long does a commercial LED installation take in Tampa, FL?',
    answer:
      'Commercial LED installation timelines in Tampa Bay depend on project scope and permit requirements. Straightforward retrofits may complete within a few weeks. Larger projects involving photometric design, permit review, and new fixture installation typically take one to three months. We provide a detailed project schedule during our initial consultation.',
  },
  {
    question: 'Does Southeast Lighting serve Hillsborough County and greater Tampa?',
    answer:
      'Yes. We serve businesses throughout Hillsborough County — including Tampa, Brandon, and Plant City — as well as Pasco County, Polk County, and beyond. Our Tampa Bay commercial LED experience covers every major county in the greater Tampa Bay market, with full-service project management for every commercial lighting application.',
  },
  {
    question: 'What should I look for in a commercial lighting company in Tampa Bay?',
    answer:
      'Look for a full-service firm providing photometric design, product specification, installation coordination, and rebate acquisition under one roof. Verify they offer signed and sealed lighting plans and have direct experience with your application type — whether parking lot, sports court, retail center, dealership, or industrial facility in the Tampa Bay area.',
  },
];

function MapSection() {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-charcoal py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-transparent via-gold/30 to-transparent" />

      <div className="mx-auto px-6 lg:px-12 xl:px-20">
        <div
          ref={ref}
          className={`transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <span className="block w-8 h-px bg-gold flex-shrink-0" />
                <span className="text-gold text-[9px] font-semibold tracking-[0.38em] uppercase">
                  Find Us
                </span>
              </div>
              <h2
                className="font-serif text-white"
                style={{ fontSize: 'clamp(1.6rem, 2.8vw, 2.4rem)', fontWeight: 300 }}
              >
                Southeast Lighting &amp; Design — Tampa Bay, FL
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href="tel:7279149002"
                className="flex items-center gap-3 group cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center border border-gold/30 group-hover:border-gold transition-colors duration-300 flex-shrink-0">
                  <i className="ri-phone-line text-gold text-sm"></i>
                </div>
                <span className="text-white/60 text-sm group-hover:text-gold transition-colors duration-300 whitespace-nowrap">
                  727-914-9002
                </span>
              </a>
              <a
                href="mailto:Info@SELightingDesign.com"
                className="flex items-center gap-3 group cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center border border-gold/30 group-hover:border-gold transition-colors duration-300 flex-shrink-0">
                  <i className="ri-mail-line text-gold text-sm"></i>
                </div>
                <span className="text-white/60 text-xs group-hover:text-gold transition-colors duration-300">
                  Info@SELightingDesign.com
                </span>
              </a>
            </div>
          </div>

          <div
            className="w-full overflow-hidden border border-white/8"
            style={{ height: '480px' }}
          >
            <iframe
              title="Southeast Lighting & Design — Google Business Listing, Tampa Bay FL"
              src="https://maps.google.com/maps?cid=17733934542755152632&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div className="mt-6 flex items-center gap-3">
            <i className="ri-information-line text-gold/50 text-sm"></i>
            <p className="text-white/25 text-xs tracking-wide">
              Serving Tampa Bay, Florida and surrounding counties. Commercial projects available throughout Florida and nationwide.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LocationPage() {
  return (
    <div className="bg-off-white">
      <Navbar />
      <HeroSection
        eyebrow="Commercial LED Lighting — Tampa Bay, Florida"
        headline="Expert Commercial Lighting Solutions Across Tampa Bay."
        subheadline="Southeast Lighting & Design is locally based in Tampa Bay, serving businesses throughout Pinellas, Hillsborough, and Pasco counties with full-service commercial LED lighting, photometric design, and rebate acquisition."
        ctaText="Get a Tampa Bay Assessment"
        ctaLink="/services"
        secondaryCta="Call 727-914-9002"
        secondaryCtaLink="tel:7279149002"
        bgImage="https://public.readdy.ai/ai/img_res/0bee05586da9364c419bbf0a4c5fa76b.jpg"
      />
      <TrustBar />
      <ServicesGrid
        eyebrow="Tampa Bay Services"
        title="Commercial Lighting Services for Tampa Bay Businesses"
        subtitle="Full-service LED lighting solutions tailored for commercial businesses across Tampa Bay and the greater Florida market — from design through rebate acquisition."
        items={LOCAL_SERVICES}
        ctaText="See All Services"
        ctaLink="/services"
      />
      <AIAnswerBlock
        eyebrow="Tampa Bay Location Info"
        title="Commercial LED Lighting in Tampa Bay, FL"
        qas={AI_QAS}
        image="https://www.selightingdesign.com/wp-content/uploads/2019/01/Safety-Harbor-Tennis-Court-2048x1000.jpg"
        imageAlt="LED sports lighting at Safety Harbor Tennis Courts — Tampa Bay, Florida"
      />
      <ProcessSection />
      <CTABand
        text="Based in Tampa Bay — ready to assess your commercial property at no obligation."
        buttonText="Get a Local Assessment"
        buttonLink="/services"
        showPhone
        variant="gold"
      />
      <WhyChooseUs
        eyebrow="Local Tampa Bay Expertise"
        headline="Your Tampa Bay Commercial Lighting Specialists."
        image="https://readdy.ai/api/search-image?query=nighttime%20aerial%20view%20of%20Tampa%20Bay%20Florida%20commercial%20area%20with%20bright%20LED%20lighting%20on%20commercial%20properties%20parking%20lots%20and%20streets%2C%20waterfront%20visible%20in%20background%2C%20warm%20golden%20city%20lights%2C%20commercial%20real%20estate%20photography%2C%20wide%20angle%20panoramic%20shot%2C%20premium%20high-end%20commercial%20district&width=1200&height=800&seq=location-why-2026-v1&orientation=landscape"
        imageAlt="Tampa Bay commercial district at night with LED lighting — Southeast Lighting & Design service area"
        benefits={LOCAL_EXPERTISE}
        ctaText="Request a Free Quote"
        ctaLink="/services"
      />
      <ServiceAreas
        title="Tampa Bay Neighborhoods & Counties Served"
        subtitle="Commercial LED lighting services available throughout the Tampa Bay region and surrounding Florida communities."
        areaGroups={TAMPA_AREA_GROUPS}
      />
      <TestimonialsSection
        eyebrow="Tampa Bay Featured Projects"
        title="Work Completed Across the Tampa Bay Region"
        projects={PROJECTS}
        ctaText="Start Your Project"
        ctaLink="/services"
      />
      <CTABand
        text="Real projects, real results — for commercial properties across the Tampa Bay region."
        buttonText="Start Your Project"
        buttonLink="/services"
        showPhone
        variant="dark"
      />
      <MapSection />
      <FAQSection
        title="Tampa Bay Lighting FAQs"
        items={FAQ_ITEMS}
        image="https://www.selightingdesign.com/wp-content/uploads/2019/02/Crown-Acura-2-2048x1000.jpg"
        imageAlt="LED site lighting at Crown Acura dealership in Clearwater, Florida"
      />
      <FinalCTA
        eyebrow="Serving Tampa Bay Businesses"
        headline="Ready for Expert Commercial Lighting in Tampa Bay?"
        subtext="Contact Southeast Lighting & Design for a complimentary commercial lighting assessment for your Tampa Bay property. We manage everything from design through final rebate acquisition."
      />
      <Footer />
    </div>
  );
}
